import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  passwordResetRequired: boolean;
  checkPasswordResetRequired: () => Promise<void>;
  signUp: (email: string, password: string, userData: { first_name: string; last_name: string, church_id: string }) => Promise<{ data: { user: User | null; session: Session | null } | null; error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);

  const checkPasswordResetRequired = async () => {
    if (!user) return;

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('password_reset_required')
        .eq('id', user.id)
        .single();

      if (!error && profile) {
        setPasswordResetRequired(profile.password_reset_required || false);
      }
    } catch (error) {
      console.error('Error checking password reset requirement:', error);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Check for password reset requirement when user signs in
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(async () => {
            try {
              const { data: profile, error } = await supabase
                .from('profiles')
                .select('password_reset_required')
                .eq('id', session.user.id)
                .single();

              if (!error && profile) {
                setPasswordResetRequired(profile.password_reset_required || false);
              }
            } catch (error) {
              console.error('Error checking password reset requirement:', error);
            }
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setPasswordResetRequired(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting session:', error);
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Check password reset requirement for existing session
      if (session?.user?.id) {
        setTimeout(async () => {
          try {
            const { data: profile, error } = await supabase
              .from('profiles')
              .select('password_reset_required')
              .eq('id', session.user.id)
              .single();

            if (!error && profile) {
              setPasswordResetRequired(profile.password_reset_required || false);
            }
          } catch (error) {
            console.error('Error checking password reset requirement:', error);
          }
        }, 0);
      }
    }).catch((error) => {
      console.error('Error in getSession:', error);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, userData: { first_name: string; last_name: string, church_id: string }) => {
    const redirectUrl = `${window.location.origin}/`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: userData
      }
    });
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Handle specific error cases with better messages
        if (error.message.includes('Invalid login credentials')) {
          return { error: { ...error, message: 'Invalid email or password. Please check your credentials and try again.' } };
        }
        if (error.message.includes('Email not confirmed')) {
          return { error: { ...error, message: 'Please check your email and click the confirmation link before signing in.' } };
        }
      }

      return { error };
    } catch (err: any) {
      return { error: err };
    }
  };

  const signOut = async () => {
    setPasswordResetRequired(false);
    await supabase.auth.signOut();
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (!error && user) {
      // Clear the password reset requirement
      await supabase
        .from('profiles')
        .update({ password_reset_required: false })
        .eq('id', user.id);

      setPasswordResetRequired(false);
    }

    return { error };
  };

  const value = {
    user,
    session,
    loading,
    passwordResetRequired,
    checkPasswordResetRequired,
    signUp,
    signIn,
    signOut,
    updatePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};